import { useState, useRef, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import QuestionLibrary from "./QuestionLibrary"
import ProfileSidebar from "./ProfileSideBar"
import toast from "react-hot-toast"
import GenerateQuestionModal from "./GenerateQuestionModal"
import InterviewSettingsModal from "./InterviewSettingsModal"
import {
  X,
  Bold,
  Italic,
  List,
  ListOrdered,
  ChevronRight,
  Plus,
  FileText,
  FileQuestion,
  ChevronUp,
  ChevronDown,
  Code2,
  Briefcase,
  Server,
  Theater,
  Trash2,
  Loader2,
  Sparkles,
  Settings,
  GripVertical,
  PenLine,
} from "lucide-react"

// ── Category config ───────────────────────────────────────────────────────────
const CATEGORIES = ["Coding", "Case Study", "System Design", "Role Play"]

const CATEGORY_ICONS = {
  Coding: Code2,
  "Case Study": Briefcase,
  "System Design": Server,
  "Role Play": Theater,
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const typeToCategory = (type) => {
  if (!type) return null
  const map = {
    coding: "Coding",
    "case-study": "Case Study",
    "system-design": "System Design",
    "role-play": "Role Play",
  }
  return map[type.toLowerCase()] ?? null
}

const buildInitialState = (existingRound, nextIdRef) => {
  const assessments = []
  const editorContents = {}
  let resumeEnabled = false
  let resumeConfig = { numQuestions: 1, numFollowUps: 2, instructions: "" }
  // Custom questions: array of { id, text }
  let customQuestions = []

  const items =
    existingRound?.assement ??
    existingRound?.assessment ??
    existingRound?.interviewPlan?.assessment ??
    existingRound?.interviewPlan?.assement ??
    []

  items.forEach((item) => {
    if (item.type === "resume-based") {
      resumeEnabled = true
      resumeConfig = {
        numQuestions: item.noOfQuestions ?? 1,
        numFollowUps: item.noOfFollowUps ?? 2,
        instructions: item.instructions ?? "",
      }
    } else if (item.type === "custom") {
      customQuestions.push({
        id: nextIdRef.current++,
        text: item.question ?? "",
        instructions: item.instructions ?? "",
      })
    } else {
      const id = nextIdRef.current++
      const category = typeToCategory(item.type)
      assessments.push({ id, category })
      editorContents[id] = item.question ?? ""
    }
  })

  return {
    assessments,
    editorContents,
    resumeEnabled,
    resumeConfig,
    customQuestions,
  }
}

// ── Toolbar button ────────────────────────────────────────────────────────────
const ToolbarBtn = ({ active, onClick, children, title }) => (
  <button
    title={title}
    onMouseDown={(e) => {
      e.preventDefault()
      onClick()
    }}
    className={`flex items-center justify-center w-7 h-7 transition-all border
      ${
        active
          ? "bg-zinc-900 border-zinc-900 text-white"
          : "bg-white border-zinc-200 text-zinc-500 hover:border-zinc-400 hover:text-zinc-900"
      }`}
    style={{ borderRadius: 0 }}
  >
    {children}
  </button>
)

// ── Number stepper ────────────────────────────────────────────────────────────
const NumberStepper = ({ value, onChange, min = 1, max = 99 }) => {
  const formatted = String(value).padStart(2, "0")
  return (
    <div
      className="flex items-center w-full border border-zinc-200 bg-white overflow-hidden"
      style={{ borderRadius: 0 }}
    >
      <div
        className="flex-1 px-3 py-2 text-sm text-zinc-800 select-none"
        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
      >
        {formatted}
      </div>
      <div className="flex flex-col border-l border-zinc-200 flex-shrink-0">
        <button
          onMouseDown={(e) => {
            e.preventDefault()
            if (value < max) onChange(value + 1)
          }}
          className="flex items-center justify-center w-7 h-[18px] hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors border-b border-zinc-200"
        >
          <ChevronUp size={10} strokeWidth={2.5} />
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault()
            if (value > min) onChange(value - 1)
          }}
          className="flex items-center justify-center w-7 h-[18px] hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors"
        >
          <ChevronDown size={10} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  )
}

// ── Toggle ────────────────────────────────────────────────────────────────────
const Toggle = ({ enabled, onToggle }) => (
  <button
    onClick={onToggle}
    className={`relative inline-flex h-6 w-11 items-center transition-colors duration-200 focus:outline-none border ${
      enabled ? "bg-zinc-900 border-zinc-900" : "bg-zinc-200 border-zinc-200"
    }`}
    style={{ borderRadius: 999 }}
  >
    <span
      className={`inline-block h-4 w-4 transform bg-white transition-transform duration-200 shadow-sm ${
        enabled ? "translate-x-6" : "translate-x-1"
      }`}
      style={{ borderRadius: 999 }}
    />
  </button>
)

// ── Rich text editor ──────────────────────────────────────────────────────────
const RichEditor = ({
  editorRef,
  placeholder = "Describe the assessment…",
  savedContent = "",
  onInput,
}) => {
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
  })

  useEffect(() => {
    const el = editorRef.current
    if (el && savedContent) {
      el.innerHTML = savedContent
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const exec = (cmd) => {
    document.execCommand(cmd, false, null)
    editorRef.current?.focus()
    updateActiveFormats()
  }

  const updateActiveFormats = () => {
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
    })
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center gap-1 px-5 py-2.5 border-b border-zinc-200 bg-white shrink-0">
        <span
          className="text-[10px] font-bold uppercase tracking-widest text-zinc-300 mr-2"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          Format
        </span>
        <ToolbarBtn
          active={activeFormats.bold}
          onClick={() => exec("bold")}
          title="Bold"
        >
          <Bold size={11} strokeWidth={2.5} />
        </ToolbarBtn>
        <ToolbarBtn
          active={activeFormats.italic}
          onClick={() => exec("italic")}
          title="Italic"
        >
          <Italic size={11} strokeWidth={2} />
        </ToolbarBtn>
        <div className="w-px h-5 bg-zinc-200 mx-1" />
        <ToolbarBtn
          active={false}
          onClick={() => exec("insertUnorderedList")}
          title="Bullet list"
        >
          <List size={11} strokeWidth={2} />
        </ToolbarBtn>
        <ToolbarBtn
          active={false}
          onClick={() => exec("insertOrderedList")}
          title="Numbered list"
        >
          <ListOrdered size={11} strokeWidth={2} />
        </ToolbarBtn>
      </div>
      <div className="flex-1 overflow-y-auto hide-scroll bg-zinc-50 relative">
        <div
          ref={editorRef}
          className="rich-editor px-5 py-5 text-zinc-700 text-[14px] leading-relaxed"
          contentEditable
          suppressContentEditableWarning
          data-placeholder={placeholder}
          onKeyUp={updateActiveFormats}
          onMouseUp={updateActiveFormats}
          onInput={() => onInput?.(editorRef.current?.innerHTML ?? "")}
          style={{
            fontFamily: "'IBM Plex Sans', sans-serif",
            minHeight: "100%",
          }}
        />
      </div>
    </div>
  )
}

// ── Resume Screening Config Panel ─────────────────────────────────────────────
const ResumeScreeningConfigPanel = ({ config, onChange }) => (
  <div className="flex flex-col flex-1 min-h-0">
    <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-white shrink-0">
      <div className="flex items-center gap-2">
        <FileQuestion size={12} className="text-zinc-400" />
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">
          Resume Based Screening
        </span>
      </div>
    </div>
    <div className="flex-1 overflow-y-auto hide-scroll bg-zinc-50 px-6 py-6">
      <div className="flex flex-col gap-6 max-w-lg">
        <div className="flex items-start gap-8">
          <div className="flex flex-col gap-2">
            <label
              className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide"
              style={{ letterSpacing: "0.05em" }}
            >
              Number of Questions
            </label>
            <NumberStepper
              value={config.numQuestions}
              onChange={(v) => onChange({ ...config, numQuestions: v })}
              min={1}
              max={20}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label
              className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide"
              style={{ letterSpacing: "0.05em" }}
            >
              Number of Follow-ups
            </label>
            <NumberStepper
              value={config.numFollowUps}
              onChange={(v) => onChange({ ...config, numFollowUps: v })}
              min={0}
              max={10}
            />
          </div>
        </div>
        <div className="border-t border-zinc-200" />
        <div className="flex flex-col gap-2">
          <label
            className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide"
            style={{ letterSpacing: "0.05em" }}
          >
            Other Instructions{" "}
            <span className="text-zinc-400 normal-case font-normal tracking-normal">
              (Optional)
            </span>
          </label>
          <textarea
            value={config.instructions}
            onChange={(e) =>
              onChange({ ...config, instructions: e.target.value })
            }
            placeholder="Ask one question about experience in B2B sales and one question about candidate's last job"
            rows={4}
            className="w-full px-3 py-2.5 text-[13px] text-zinc-700 border border-zinc-200 bg-white resize-none focus:outline-none focus:border-zinc-400 placeholder-zinc-300 leading-relaxed"
            style={{
              fontFamily: "'IBM Plex Sans', sans-serif",
              borderRadius: 0,
            }}
          />
        </div>
      </div>
    </div>
  </div>
)

// ── Resume Based Questions toggle ─────────────────────────────────────────────
const ResumeBasedQuestionsToggle = ({
  enabled,
  onToggle,
  onPanelOpen,
  numQuestions,
}) => {
  const estimatedMins = numQuestions * 4
  return (
    <div className="mx-0 border-b border-zinc-100">
      <button
        type="button"
        onClick={onPanelOpen}
        className="w-full flex items-start justify-between px-5 py-4 text-left hover:bg-zinc-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 border border-zinc-200 flex items-center justify-center flex-shrink-0 bg-zinc-50">
            <FileQuestion size={13} className="text-zinc-500" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-zinc-800">
                Resume Based Questions
              </span>
              {enabled && (
                <span
                  className="text-[10px] text-zinc-400 font-medium"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  ⊙ {estimatedMins} Mins
                </span>
              )}
            </div>
            <p className="text-[13px] text-zinc-500 mt-0.5 leading-snug">
              Personalized questions based on the candidates' resume
            </p>
          </div>
        </div>
        <div
          onClick={(e) => e.stopPropagation()}
          className="flex-shrink-0 mt-0.5"
        >
          <Toggle enabled={enabled} onToggle={onToggle} />
        </div>
      </button>
    </div>
  )
}

// ── Add / Edit Question Modal ─────────────────────────────────────────────────
const AddQuestionModal = ({ onAdd, onClose, initialQuestion = null }) => {
  const [text, setText] = useState(initialQuestion?.text ?? "")
  const [instructions, setInstructions] = useState(initialQuestion?.instructions ?? "")
  const isEditing = initialQuestion !== null

  const handleAdd = () => {
    const trimmed = text.trim()
    if (!trimmed) return
    onAdd({ text: trimmed, instructions: instructions.trim() })
    onClose()
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-6"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <div
          className="bg-white border border-zinc-200 shadow-2xl flex flex-col"
          style={{ borderRadius: 0, width: "min(560px, 95vw)" }}
        >
          <div className="flex items-start justify-between px-6 py-4 border-b border-zinc-200">
            <div>
              <p className="text-sm font-bold text-zinc-900">
                {isEditing ? "Edit Custom Question" : "Add Custom Question"}
              </p>
              <p className="text-[13px] text-zinc-500 mt-0.5">
                Write a question for the AI interviewer to ask
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-zinc-100 transition-colors text-zinc-400 hover:text-zinc-700"
              style={{ borderRadius: 0 }}
            >
              <X size={15} />
            </button>
          </div>
          <div className="p-6 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label
                className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide"
                style={{ letterSpacing: "0.05em" }}
              >
                Question
              </label>
              <textarea
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. Tell me about a time you had to make a difficult technical decision under pressure…"
                rows={4}
                className="w-full px-4 py-3 text-[14px] text-zinc-700 border border-zinc-200 bg-zinc-50 resize-none focus:outline-none focus:border-zinc-400 placeholder-zinc-500 leading-relaxed"
                style={{
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  borderRadius: 0,
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleAdd()
                }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label
                className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide"
                style={{ letterSpacing: "0.05em" }}
              >
                Additional Instructions (Optional)
              </label>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="e.g. Probe for specific examples, ask follow-ups about impact and scale…"
                rows={3}
                className="w-full px-4 py-3 text-[14px] text-zinc-700 border border-zinc-200 bg-zinc-50 resize-none focus:outline-none focus:border-zinc-400 placeholder-zinc-500 leading-relaxed"
                style={{
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  borderRadius: 0,
                }}
              />
            </div>
            <p
              className="text-[12px] text-zinc-400"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              Tip: Press ⌘↵ to {isEditing ? "save" : "add"} quickly
            </p>
          </div>
          <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-zinc-500 border border-zinc-200 hover:border-zinc-400 hover:text-zinc-700 transition-all"
              style={{ borderRadius: 0 }}
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={!text.trim()}
              className={`flex items-center gap-2 px-5 py-2 text-xs font-bold uppercase tracking-wide transition-all border ${
                text.trim()
                  ? "bg-zinc-900 border-zinc-900 text-white hover:bg-zinc-800"
                  : "bg-zinc-100 border-zinc-100 text-zinc-300 cursor-not-allowed"
              }`}
              style={{ borderRadius: 0 }}
            >
              <Plus size={11} />
              {isEditing ? "Save Changes" : "Add Question"}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// ── Custom Questions Right Panel ──────────────────────────────────────────────
const CustomQuestionsPanel = ({
  questions,
  onDeleteQuestion,
  onEditQuestion,
  onReorderQuestions,
  onOpenAddModal,
  onOpenGenerateModal,
}) => {
  const dragItem = useRef(null)
  const dragOverItem = useRef(null)
  const [dragging, setDragging] = useState(false)

  const handleDragStart = (index) => {
    dragItem.current = index
    setDragging(true)
  }
  const handleDragEnter = (index) => {
    dragOverItem.current = index
  }
  const handleDragEnd = () => {
    if (
      dragItem.current !== null &&
      dragOverItem.current !== null &&
      dragItem.current !== dragOverItem.current
    ) {
      const updated = [...questions]
      const dragged = updated.splice(dragItem.current, 1)[0]
      updated.splice(dragOverItem.current, 0, dragged)
      onReorderQuestions(updated)
    }
    dragItem.current = null
    dragOverItem.current = null
    setDragging(false)
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-white shrink-0">
        <div className="flex items-center gap-2">
          <PenLine size={12} className="text-zinc-500" />
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">
            Custom Questions
          </span>
          {questions.length > 0 && (
            <span
              className="text-[13px] font-medium text-zinc-500 ml-1"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              · {questions.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onOpenGenerateModal}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 bg-white text-zinc-600 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white transition-all"
            style={{ borderRadius: 0 }}
          >
            <Sparkles size={10} />
            <span className="text-[11px] font-bold uppercase tracking-widest">
              Generate
            </span>
          </button>
          <button
            onClick={onOpenAddModal}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-800 transition-all"
            style={{ borderRadius: 0 }}
          >
            <Plus size={10} />
            <span className="text-[11px] font-bold uppercase tracking-widest">
              Add Question
            </span>
          </button>
        </div>
      </div>

      {/* Questions list */}
      <div className="flex-1 overflow-y-auto hide-scroll bg-zinc-50 px-5 py-5">
        {questions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-8">
            <div className="w-10 h-10 border border-zinc-200 flex items-center justify-center bg-white">
              <PenLine size={16} className="text-zinc-400" />
            </div>
            <div>
              <p className="text-[13px] font-bold uppercase tracking-widest text-zinc-500">
                No custom questions yet
              </p>
              <p className="text-[13px] text-zinc-500 mt-1 leading-snug">
                Add a question manually or generate with AI
              </p>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={onOpenGenerateModal}
                className="flex items-center gap-1.5 px-3 py-2 border border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400 hover:text-zinc-700 transition-all"
                style={{ borderRadius: 0 }}
              >
                <Sparkles size={10} />
                <span className="text-[11px] font-bold uppercase tracking-widest">
                  Generate
                </span>
              </button>
              <button
                onClick={onOpenAddModal}
                className="flex items-center gap-1.5 px-3 py-2 border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-800 transition-all"
                style={{ borderRadius: 0 }}
              >
                <Plus size={10} />
                <span className="text-[11px] font-bold uppercase tracking-widest">
                  Add Question
                </span>
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {questions.map((q, idx) => (
              <div
                key={q.id}
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragEnter={() => handleDragEnter(idx)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
                className="flex items-start gap-3 px-4 py-3.5 bg-white border border-zinc-200 group hover:border-zinc-300 transition-all cursor-default"
                style={{
                  borderRadius: 0,
                  opacity: dragging && dragItem.current === idx ? 0.5 : 1,
                }}
              >
                {/* Drag handle */}
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-shrink-0 flex items-center justify-center w-5 h-5 mt-0.5 cursor-grab active:cursor-grabbing text-zinc-400 hover:text-zinc-600 transition-colors"
                  title="Drag to reorder"
                >
                  <GripVertical size={13} strokeWidth={2} />
                </button>
                {/* Number */}
                <span
                  className="flex-shrink-0 text-[13px] font-bold text-zinc-400 mt-0.5 tabular-nums"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  {String(idx + 1).padStart(2, "0")}
                </span>
                {/* Text + instructions */}
                <div className="flex-1 flex flex-col gap-1 min-w-0">
                  <p
                    className="text-[14px] text-zinc-700 leading-relaxed"
                    style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                  >
                    {q.text}
                  </p>
                  {q.instructions && (
                    <p
                      className="text-[12px] text-zinc-400 leading-relaxed italic"
                      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                    >
                      {q.instructions}
                    </p>
                  )}
                </div>
                {/* Edit + Delete */}
                <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">
                  <button
                    onClick={() => onEditQuestion(q)}
                    className="flex items-center justify-center w-5 h-5 text-zinc-400 hover:text-blue-500 rounded-sm hover:bg-blue-50 transition-colors"
                    title="Edit question"
                  >
                    <PenLine size={10} strokeWidth={2} />
                  </button>
                  <button
                    onClick={() => onDeleteQuestion(q.id)}
                    className="flex items-center justify-center w-5 h-5 text-zinc-400 hover:text-red-500 rounded-sm hover:bg-red-50 transition-colors"
                    title="Remove question"
                  >
                    <Trash2 size={10} strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Additional Instructions Modal ─────────────────────────────────────────────
const AdditionalInstructionsModal = ({ value, onSave, onClose }) => {
  const [text, setText] = useState(value)

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-6"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <div
          className="bg-white border border-zinc-200 shadow-2xl flex flex-col"
          style={{ borderRadius: 0, width: "min(580px, 95vw)" }}
        >
          <div className="flex items-start justify-between px-6 py-4 border-b border-zinc-200">
            <div>
              <p className="text-sm font-bold text-zinc-900">
                Additional Instructions
              </p>
              <p className="text-[14px] text-zinc-500 mt-0.5">
                Top-level guidance passed to the AI interviewer for the entire
                round
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-zinc-100 transition-colors text-zinc-400 hover:text-zinc-700"
              style={{ borderRadius: 0 }}
            >
              <X size={15} />
            </button>
          </div>
          <div className="p-6">
            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. Before the interview starts, please explain the role and the company. 

Company Info:
We are a venture capital firm that invests in early-stage technology startups across AI, fintech, and enterprise software. Our mission is to partner with ambitious founders and help them scale their businesses through capital, strategic guidance, and access to our global network of operators and investors.

Role – VC Associate:
As a Venture Capital Associate, you will support the investment team in sourcing promising startups, conducting market and financial analysis, and evaluating potential deals. You will also work closely with portfolio companies, helping founders with research, strategy, and growth initiatives."
              rows={6}
              className="w-full px-4 py-3 text-[14px] text-zinc-700 border border-zinc-200 bg-zinc-50 resize-none focus:outline-none focus:border-zinc-400 placeholder-zinc-400 leading-relaxed"
              style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                borderRadius: 0,
              }}
            />
            <p
              className="text-[12px] text-zinc-400 mt-3"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              This applies to the whole interview round, not individual
              questions.
            </p>
          </div>
          <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-zinc-500 border border-zinc-200 hover:border-zinc-400 hover:text-zinc-700 transition-all"
              style={{ borderRadius: 0 }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onSave(text.trim())
                onClose()
              }}
              className="flex items-center gap-2 px-5 py-2 text-xs font-bold uppercase tracking-wide bg-zinc-900 border border-zinc-900 text-white hover:bg-zinc-800 transition-all"
              style={{ borderRadius: 0 }}
            >
              Save Instructions
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// ── Category Picker Modal ─────────────────────────────────────────────────────
const CategoryPickerModal = ({ onSelect, onClose }) => {
  const [hovered, setHovered] = useState(null)
  const descriptions = {
    Coding: "Algorithmic problems, data structures, and implementation tasks",
    "Case Study": "Real-world business scenarios and analytical challenges",
    "System Design": "Architecture, scalability, and infrastructure questions",
    "Role Play": "Situational and behavioural interview simulations",
  }
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-6"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <div
          className="bg-white border border-zinc-200 shadow-2xl flex flex-col"
          style={{ borderRadius: 0, width: "min(520px, 95vw)" }}
        >
          <div className="flex items-start justify-between px-6 py-4 border-b border-zinc-200">
            <div>
              <p className="text-sm font-bold text-zinc-900">Add Assessment</p>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                Choose a category for the new assessment variation
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-zinc-100 transition-colors text-zinc-400 hover:text-zinc-700"
              style={{ borderRadius: 0 }}
            >
              <X size={15} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 p-5">
            {CATEGORIES.map((cat) => {
              const Icon = CATEGORY_ICONS[cat]
              return (
                <button
                  key={cat}
                  onClick={() => onSelect(cat)}
                  onMouseEnter={() => setHovered(cat)}
                  onMouseLeave={() => setHovered(null)}
                  className={`flex flex-col items-start gap-3 p-4 border text-left transition-all group ${
                    hovered === cat
                      ? "border-zinc-900 bg-zinc-900"
                      : "border-zinc-200 bg-white hover:border-zinc-900"
                  }`}
                  style={{ borderRadius: 0 }}
                >
                  <div
                    className={`w-8 h-8 border flex items-center justify-center flex-shrink-0 transition-colors ${
                      hovered === cat
                        ? "border-zinc-600 bg-zinc-800"
                        : "border-zinc-200 bg-zinc-50"
                    }`}
                  >
                    <Icon
                      size={14}
                      className={
                        hovered === cat ? "text-zinc-200" : "text-zinc-500"
                      }
                    />
                  </div>
                  <div>
                    <p
                      className={`text-xs font-bold uppercase tracking-widest transition-colors ${
                        hovered === cat ? "text-white" : "text-zinc-800"
                      }`}
                    >
                      {cat}
                    </p>
                    <p
                      className={`text-[11px] mt-1 leading-snug transition-colors ${
                        hovered === cat ? "text-zinc-400" : "text-zinc-400"
                      }`}
                    >
                      {descriptions[cat]}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
          <div className="px-6 py-3 border-t border-zinc-100 bg-zinc-50">
            <p className="text-[10px] text-zinc-400 uppercase tracking-widest font-bold">
              The AI interviewer will tailor questions to the selected category
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

// ── Display labels ────────────────────────────────────────────────────────────
const buildDisplayLabels = (assessments) => {
  const topicCount = {}
  return assessments.reduce((acc, a) => {
    const key = a.category ?? "__custom__"
    topicCount[key] = (topicCount[key] || 0) + 1
    const base = key === "__custom__" ? "Custom Question" : a.category
    acc[a.id] = `${base} - ${topicCount[key]}`
    return acc
  }, {})
}

// ── Page component ────────────────────────────────────────────────────────────
const CreateAiInterview = () => {
  const { orgId, jobId } = useParams()
  const navigate = useNavigate()

  // ── Fetch state ─────────────────────────────────────────────────────────────
  const [isFetching, setIsFetching] = useState(true)
  const [fetchError, setFetchError] = useState(null)
  const [jobName, setJobName] = useState("Job")
  const [existingRoundId, setExistingRoundId] = useState(null)

  // ── Assessment state (initialised after fetch) ───────────────────────────────
  const nextId = useRef(1)
  const [assessments, setAssessments] = useState([])
  const [editorContents, setEditorContents] = useState({})
  const [resumeScreeningEnabled, setResumeScreeningEnabled] = useState(false)
  const [resumeConfig, setResumeConfig] = useState({
    numQuestions: 1,
    numFollowUps: 2,
    instructions: "",
  })
  const [activeId, setActiveId] = useState(null)
  const [rightPanel, setRightPanel] = useState("editor") // "editor" | "resume" | "custom"

  // Custom questions: flat list of { id, text }
  const [customQuestions, setCustomQuestions] = useState([])
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false)

  const editorRefs = useRef({})
  const initEditorContents = useRef({})

  const [showQuestionLibrary, setShowQuestionLibrary] = useState(false)
  const [showCategoryPicker, setShowCategoryPicker] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [showSettingsModal, setShowSettingsModal] = useState(false)

  const [editingQuestion, setEditingQuestion] = useState(null)
  const [additionalInstructions, setAdditionalInstructions] = useState("")
  const [showInstructionsModal, setShowInstructionsModal] = useState(false)

  const [interviewSettings, setInterviewSettings] = useState({
    duration: 30,
    skills: [],
  })

  const CATEGORY_DURATION = {
    Coding: 10,
    "System Design": 20,
    "Case Study": 20,
    "Role Play": 10,
  }

  const getAssessmentDuration = (a) => CATEGORY_DURATION[a.category] ?? 3

  const dragItem = useRef(null)
  const dragOverItem = useRef(null)

  const handleDragStart = (index) => {
    dragItem.current = index
  }

  const handleDragEnter = (index) => {
    dragOverItem.current = index
  }

  const handleDragEnd = () => {
    const updated = [...assessments]
    const dragged = updated.splice(dragItem.current, 1)[0]
    updated.splice(dragOverItem.current, 0, dragged)
    dragItem.current = null
    dragOverItem.current = null
    setAssessments(updated)
  }

  // ── Fetch job + existing AI round on mount ──────────────────────────────────
  useEffect(() => {
    const fetchJobData = async () => {
      setIsFetching(true)
      setFetchError(null)
      try {
        const response = await fetch(
          `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}/lite`,
        )
        if (!response.ok)
          throw new Error(`Failed to fetch job data (${response.status})`)
        const data = await response.json()

        const jobData = data.data ?? data
        setJobName(
          jobData.role ??
            jobData.jobTitle ??
            jobData.jobName ??
            jobData.title ??
            "Job",
        )

        const rounds =
          jobData.interviewStructure?.rounds ??
          (Array.isArray(jobData.interviewStructure)
            ? jobData.interviewStructure
            : [])

        const aiRound =
          rounds.find((r) => {
            const t = (r.roundType ?? "").toLowerCase().trim()
            return t === "ai" || t === "ai only" || t === "ai-only"
          }) ?? null

        if (aiRound?.roundId) setExistingRoundId(aiRound.roundId)

        const init = aiRound
          ? buildInitialState(aiRound, nextId)
          : {
              assessments: [],
              editorContents: {},
              resumeEnabled: false,
              resumeConfig: {
                numQuestions: 1,
                numFollowUps: 2,
                instructions: "",
              },
              customQuestions: [],
            }

        setAssessments(init.assessments)
        setEditorContents(init.editorContents)
        setResumeScreeningEnabled(init.resumeEnabled)
        setResumeConfig(init.resumeConfig)
        setCustomQuestions(init.customQuestions ?? [])
        setInterviewSettings({
          duration: aiRound?.duration ?? 30,
          skills: aiRound?.skillsToEvaluate ?? [],
        })
        setAdditionalInstructions(aiRound?.additionalInstructions ?? "")
        setActiveId(init.assessments[0]?.id ?? null)
        setRightPanel(
          init.resumeEnabled && init.assessments.length === 0
            ? "resume"
            : init.assessments.length > 0
              ? "editor"
              : (init.customQuestions?.length ?? 0) > 0
                ? "custom"
                : "editor",
        )
        initEditorContents.current = init.editorContents
      } catch (err) {
        setFetchError(err.message ?? "Failed to load job data.")
      } finally {
        setIsFetching(false)
      }
    }

    if (jobId && orgId) {
      fetchJobData()
    } else {
      setIsFetching(false)
    }
  }, [jobId, orgId])

  // After fetch, push existing HTML into contentEditable refs
  useEffect(() => {
    if (isFetching) return
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        Object.entries(initEditorContents.current).forEach(([id, html]) => {
          if (!html) return
          const el = editorRefs.current[Number(id)]?.current
          if (el) el.innerHTML = html
        })
      })
    })
  }, [isFetching])

  // ── Editor helpers ──────────────────────────────────────────────────────────
  const saveActiveEditorContent = () => {
    const el = editorRefs.current[activeId]?.current
    if (el) {
      setEditorContents((prev) => ({ ...prev, [activeId]: el.innerHTML }))
    }
  }

  const handleToggleResumeScreening = () => {
    if (!resumeScreeningEnabled) {
      saveActiveEditorContent()
      setResumeScreeningEnabled(true)
      setRightPanel("resume")
    } else if (rightPanel === "editor" || rightPanel === "custom") {
      saveActiveEditorContent()
      setRightPanel("resume")
    } else {
      setResumeScreeningEnabled(false)
      setRightPanel(assessments.length > 0 ? "editor" : "custom")
    }
  }

  const handleAssessmentClick = (id) => {
    saveActiveEditorContent()
    setActiveId(id)
    setRightPanel("editor")
  }

  const handleCustomQuestionsTileClick = () => {
    saveActiveEditorContent()
    setRightPanel("custom")
  }

  const handleDeleteAssessment = (id) => {
    setAssessments((prev) => {
      const next = prev.filter((a) => a.id !== id)
      if (id === activeId) {
        if (next.length === 0) {
          setActiveId(null)
          setRightPanel("custom")
        } else {
          const deletedIdx = prev.findIndex((a) => a.id === id)
          const newActive = next[Math.min(deletedIdx, next.length - 1)]
          setActiveId(newActive.id)
          setRightPanel("editor")
        }
      }
      return next
    })
    setEditorContents((prev) => {
      const n = { ...prev }
      delete n[id]
      return n
    })
    delete editorRefs.current[id]
  }

  const handleDeleteCustomQuestion = (id) => {
    setCustomQuestions((prev) => prev.filter((q) => q.id !== id))
  }

  const handleAddCustomQuestion = ({ text, instructions }) => {
    const id = nextId.current++
    setCustomQuestions((prev) => [...prev, { id, text, instructions }])
  }

  const handleEditCustomQuestion = ({ text, instructions }) => {
    setCustomQuestions((prev) =>
      prev.map((q) =>
        q.id === editingQuestion.id ? { ...q, text, instructions } : q,
      ),
    )
    setEditingQuestion(null)
  }

  const handleReorderCustomQuestions = (reordered) => {
    setCustomQuestions(reordered)
  }

  const getEditorRef = (id) => {
    if (!editorRefs.current[id]) editorRefs.current[id] = { current: null }
    return editorRefs.current[id]
  }

  const handleCategorySelect = (category) => {
    // Enforce: only one skill-based assessment type allowed at a time
    const existingCategories = [
      ...new Set(assessments.map((a) => a.category).filter(Boolean)),
    ]
    if (
      existingCategories.length > 0 &&
      !existingCategories.includes(category)
    ) {
      setShowCategoryPicker(false)
      toast.error(
        `Only one assessment type allowed. Remove the existing "${existingCategories[0]}" assessment first.`,
        {
          className:
            "!font-mono !text-[11px] !rounded-none !font-medium !tracking-wide",
        },
      )
      return
    }
    setShowCategoryPicker(false)
    const id = nextId.current++
    setAssessments((prev) => [...prev, { id, category }])
    setEditorContents((prev) => ({ ...prev, [id]: "" }))
    saveActiveEditorContent()
    setActiveId(id)
    setRightPanel("editor")
  }

  const displayLabels = buildDisplayLabels(assessments)

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const activeEl = editorRefs.current[activeId]?.current
    const latestContents = { ...editorContents }
    if (activeEl) latestContents[activeId] = activeEl.innerHTML

    const emptyAssessments = assessments.filter((a) => {
      const html = latestContents[a.id] ?? ""
      return html.trim() === ""
    })

    if (emptyAssessments.length > 0) {
      const labels = buildDisplayLabels(assessments)
      const names = emptyAssessments.map((a) => labels[a.id]).join(", ")
      toast.error(`Please add questions for: ${names}`, {
        className:
          "!font-mono !text-[11px] !rounded-none !font-medium !tracking-wide",
      })
      return
    }

    if (interviewSettings.skills.length < 3) {
      toast.error(
        "Please add at least 3 skills to evaluate in Interview Settings.",
        {
          className:
            "!font-mono !text-[11px] !rounded-none !font-medium !tracking-wide",
        },
      )
      return
    }

    if (!interviewSettings.duration) {
      toast.error(
        "Please configure interview duration in Interview Settings.",
        {
          className:
            "!font-mono !text-[11px] !rounded-none !font-medium !tracking-wide",
        },
      )
      return
    }

    const durationNum = interviewSettings.duration

    if (durationNum < 10 || durationNum > 45) {
      toast.error("Total duration must be between 10 and 45 minutes.", {
        className:
          "!font-mono !text-[11px] !rounded-none !font-medium !tracking-wide",
      })
      return
    }

    const assessmentItems = []

    if (resumeScreeningEnabled) {
      assessmentItems.push({
        id: Math.random().toString(36).substring(2, 8),
        type: "resume-based",
        noOfQuestions: resumeConfig.numQuestions,
        noOfFollowUps: resumeConfig.numFollowUps,
        ...(resumeConfig.instructions.trim()
          ? { instructions: resumeConfig.instructions.trim() }
          : {}),
      })
    }

    customQuestions.forEach((q) => {
      assessmentItems.push({
        id: Math.random().toString(36).substring(2, 8),
        type: "custom",
        question: q.text,
        ...(q.instructions ? { instructions: q.instructions } : {}),
      })
    })

    assessments.forEach((a) => {
      const html = latestContents[a.id] ?? ""
      assessmentItems.push({
        id: Math.random().toString(36).substring(2, 8),
        type: a.category
          ? a.category.toLowerCase().replace(/\s+/g, "-")
          : "custom",
        ...(html.trim() ? { question: html } : {}),
      })
    })

    const payload = {
      role: jobName,
      roundId: existingRoundId ?? `round-1`,
      duration: interviewSettings.duration,
      additionalInstructions: additionalInstructions,
      ...(interviewSettings.skills.length > 0
        ? { skillsToEvaluate: interviewSettings.skills }
        : {}),
      assessment: assessmentItems,
    }

    try {
      setIsSubmitting(true)
      const response = await fetch(
        `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}/create-ai-interview-plan`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      )
      if (!response.ok) throw new Error(`Server error: ${response.status}`)
      toast.success("Interview round saved!", {
        className:
          "!font-mono !text-[11px] !rounded-none !font-medium !tracking-wide",
      })
      navigate("/jobs")
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.", {
        className:
          "!font-mono !text-[11px] !rounded-none !font-medium !tracking-wide",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Loading state ───────────────────────────────────────────────────────────
  if (isFetching) {
    return (
      <div
        className="min-h-screen bg-zinc-50 flex"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        `}</style>
        <div className="border-r border-zinc-200 bg-white overflow-y-auto min-h-screen">
          <ProfileSidebar activeTab="jobs" setActiveTab={() => {}} />
        </div>
        <div className="flex-1 ml-60 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={20} className="animate-spin text-zinc-400" />
            <p
              className="text-sm text-zinc-600"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              Loading interview plan…
            </p>
          </div>
        </div>
      </div>
    )
  }

  // ── Determine right panel content ───────────────────────────────────────────
  const showCustomPanel = rightPanel === "custom"
  const showResumePanel = rightPanel === "resume"
  const showEditorPanel = rightPanel === "editor" && assessments.length > 0
  const showEmptyEditor =
    rightPanel === "editor" && assessments.length === 0 && !showCustomPanel

  // Custom tile: shown always (even when no skill-based questions yet)
  const isCustomTileActive = rightPanel === "custom"

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-zinc-50 flex"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        .hide-scroll::-webkit-scrollbar { display: none; }
        .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        .rich-editor:empty:before { content: attr(data-placeholder); color: #aaa; pointer-events: none; }
        .rich-editor:focus { outline: none; }
        .rich-editor ul { list-style-type: disc; padding-left: 1.4em; margin: 0.4em 0; }
        .rich-editor ol { list-style-type: decimal; padding-left: 1.4em; margin: 0.4em 0; }
        .rich-editor li { margin: 0.15em 0; }
        .rich-editor p { margin: 0 0 0.65em; }
        .rich-editor b, .rich-editor strong { font-weight: 700; }
        .rich-editor i, .rich-editor em { font-style: italic; }
      `}</style>

      {/* Sidebar */}
      <div className="border-r border-zinc-200 bg-white overflow-y-auto min-h-screen">
        <ProfileSidebar activeTab="jobs" setActiveTab={() => {}} />
      </div>

      {/* Main content */}
      <div className="flex-1 ml-60 flex flex-col overflow-hidden h-screen">
        {/* Top bar */}
        <div className="bg-white px-8 py-4 flex items-center justify-between border-b border-zinc-200 shrink-0">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <p className="text-sm font-bold text-zinc-900">AI Interview</p>
                <span
                  className="text-[9px] font-bold uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5"
                  style={{ borderRadius: 0 }}
                >
                  Editing
                </span>
              </div>
              <p className="text-[13px] text-zinc-500">{jobName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {fetchError && (
              <p
                className="text-[11px] text-amber-500 max-w-xs"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                ⚠ {fetchError} — using defaults
              </p>
            )}
            <button
              onClick={() => navigate("/jobs")}
              className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors text-zinc-400 hover:text-zinc-700"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Two-column body */}
        <div className="flex flex-1 min-h-0">
          {/* LEFT PANEL */}
          <div
            className="flex flex-col border-r border-zinc-200 bg-white shrink-0"
            style={{ width: "40%" }}
          >
            {/* Interview Settings */}
            <div className="mx-0 border-b border-zinc-100">
              <button
                type="button"
                onClick={() => setShowSettingsModal(true)}
                className="w-full flex items-start justify-between px-5 py-4 text-left hover:bg-zinc-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 border border-zinc-200 flex items-center justify-center flex-shrink-0 bg-zinc-50">
                    <Settings size={13} className="text-zinc-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-zinc-800">
                        Interview Settings
                      </span>
                      {(interviewSettings.duration ||
                        interviewSettings.skills.length > 0) && (
                        <span
                          className="text-[10px] text-zinc-400 font-medium"
                          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                        >
                          ⊙ {interviewSettings.duration} Mins
                          {interviewSettings.skills.length > 0 &&
                            ` · ${interviewSettings.skills.length} skill${interviewSettings.skills.length > 1 ? "s" : ""}`}
                        </span>
                      )}
                    </div>
                    {interviewSettings.skills.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {interviewSettings.skills.map((skill) => (
                          <span
                            key={skill}
                            className="text-[10px] font-bold uppercase tracking-widest text-blue-700 py-0.5"
                            style={{
                              borderRadius: 0,
                              fontFamily: "'IBM Plex Mono', monospace",
                            }}
                          >
                            {skill},
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[13px] text-zinc-500 mt-0.5 leading-snug">
                        Duration and skills to evaluate
                      </p>
                    )}
                  </div>
                </div>
                <ChevronRight
                  size={13}
                  className="text-zinc-300 mt-0.5 flex-shrink-0"
                />
              </button>
            </div>

            {/* Resume Based Questions */}
            <ResumeBasedQuestionsToggle
              enabled={resumeScreeningEnabled}
              onToggle={handleToggleResumeScreening}
              onPanelOpen={() => {
                saveActiveEditorContent()
                setRightPanel("resume")
              }}
              numQuestions={resumeConfig.numQuestions}
            />

            {/* Skill Based Questions section */}
            <div className="flex flex-col flex-1 min-h-0 pt-4">
              <div className="px-5 mb-3 flex items-center justify-between">
                <span className="text-[13px] font-bold uppercase tracking-widest text-zinc-500">
                  Skill Based Questions
                </span>
              </div>

              <div className="flex-1 overflow-y-auto hide-scroll px-3">
                {/* Custom Questions Tile — always shown */}
                <div
                  className={`w-full flex items-start gap-2 px-3 py-3 mb-2 border transition-all cursor-pointer
                    ${isCustomTileActive ? "bg-blue-50 border-blue-200" : "bg-white border-zinc-100 hover:border-zinc-300"}`}
                  style={{ borderRadius: 0 }}
                  onClick={handleCustomQuestionsTileClick}
                >
                  {/* Icon */}
                  <div
                    className={`flex-shrink-0 flex items-center justify-center w-5 h-5 mt-0.5 ${isCustomTileActive ? "text-blue-400" : "text-zinc-500"}`}
                  >
                    <PenLine size={13} strokeWidth={2} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                    <span
                      className={`text-[14px] font-semibold leading-snug ${isCustomTileActive ? "text-blue-800" : "text-zinc-700"}`}
                    >
                      Custom Questions
                    </span>
                    {customQuestions.length === 0 ? (
                      <p
                        className={`text-[13px] ${isCustomTileActive ? "text-blue-500" : "text-zinc-500"}`}
                      >
                        No questions added yet
                      </p>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {customQuestions.map((q, idx) => (
                          <div
                            key={q.id}
                            className="flex items-baseline gap-2 min-w-0"
                          >
                            <span
                              className={`flex-shrink-0 text-[11px] font-bold tabular-nums ${isCustomTileActive ? "text-blue-400" : "text-zinc-500"}`}
                              style={{
                                fontFamily: "'IBM Plex Mono', monospace",
                              }}
                            >
                              {String(idx + 1).padStart(2, "0")}
                            </span>
                            <span
                              className={`text-[14px] leading-snug min-w-0 ${isCustomTileActive ? "text-blue-700" : "text-zinc-600"}`}
                              style={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                display: "block",
                              }}
                            >
                              {q.text}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {customQuestions.length > 0 && (
                      <span
                        className={`text-[11px] font-bold uppercase tracking-widest ${isCustomTileActive ? "text-blue-400" : "text-zinc-500"}`}
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                      >
                        {customQuestions.length} question
                        {customQuestions.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                </div>

                {/* Skill-based assessment tiles */}
                {assessments.map((a, index) => {
                  const isActive = activeId === a.id && rightPanel === "editor"
                  return (
                    <div
                      key={a.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragEnter={() => handleDragEnter(index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => e.preventDefault()}
                      className={`w-full flex items-start gap-1.5 px-3 py-3 mb-2 border transition-all cursor-pointer
                        ${isActive ? "bg-blue-50 border-blue-200" : "bg-white border-zinc-100 hover:border-zinc-300"}`}
                      style={{ borderRadius: 0 }}
                      onClick={() => handleAssessmentClick(a.id)}
                    >
                      {/* Drag handle */}
                      <button
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        className={`flex-shrink-0 flex items-center justify-center w-5 h-5 mt-0.5 cursor-grab active:cursor-grabbing transition-colors ${
                          isActive
                            ? "text-blue-400 hover:text-blue-600"
                            : "text-zinc-400 hover:text-zinc-600"
                        }`}
                        title="Drag to reorder"
                      >
                        <GripVertical size={13} strokeWidth={2} />
                      </button>

                      {/* Content */}
                      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                        <p
                          className={`text-[13px] leading-snug ${isActive ? "text-blue-800" : "text-zinc-700"}`}
                          style={{
                            fontFamily: "'Satoshi', sans-serif",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                          dangerouslySetInnerHTML={{
                            __html:
                              editorContents[a.id]
                                ?.replace(/<[^>]+>/g, " ")
                                .trim() || "No question added yet…",
                          }}
                        />
                        <div className="flex items-center gap-2">
                          {a.category && (
                            <span
                              className={`text-[11px] font-bold uppercase tracking-widest ${isActive ? "text-blue-500" : "text-zinc-500"}`}
                              style={{
                                fontFamily: "'IBM Plex Mono', monospace",
                              }}
                            >
                              {a.category}
                            </span>
                          )}
                          <span
                            className={`text-[13px] font-medium ${isActive ? "text-blue-500" : "text-zinc-500"}`}
                            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                          >
                            ⊙ {getAssessmentDuration(a)} min
                          </span>
                        </div>
                      </div>

                      {/* Delete */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteAssessment(a.id)
                        }}
                        className={`flex-shrink-0 flex items-center justify-center w-5 h-5 transition-colors rounded-sm hover:bg-red-50 mt-0.5 ${
                          isActive
                            ? "text-blue-400 hover:text-red-500"
                            : "text-zinc-400 hover:text-red-500"
                        }`}
                        title="Remove assessment"
                      >
                        <Trash2 size={10} strokeWidth={2} />
                      </button>
                    </div>
                  )
                })}

                {/* Additional Instructions Tile — always last */}
                <div
                  className="w-full flex items-start gap-2 px-3 py-3 mb-2 border transition-all cursor-pointer bg-white border-zinc-100 hover:border-zinc-300"
                  style={{ borderRadius: 0 }}
                  onClick={() => setShowInstructionsModal(true)}
                >
                  <div className="flex-shrink-0 flex items-center justify-center w-5 h-5 mt-0.5 text-zinc-500">
                    <PenLine size={13} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                    <span className="text-[14px] font-semibold leading-snug text-zinc-700">
                      Additional Instructions (Optional)
                    </span>
                    {!additionalInstructions ? (
                      <p className="text-[13px] text-zinc-500">
                        No instructions added yet
                      </p>
                    ) : (
                      <p
                        className="text-[13px] text-zinc-600 leading-snug"
                        style={{
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {additionalInstructions}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Add Assessment button */}
              <div className="px-3 py-4 border-t border-zinc-100">
                <button
                  onClick={() => setShowCategoryPicker(true)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 border border-dashed border-zinc-300 text-zinc-400 hover:border-zinc-600 hover:text-zinc-700 transition-all"
                  style={{ borderRadius: 0 }}
                >
                  <Plus size={11} />
                  <span className="text-xs font-semibold">Add Assessment</span>
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
            {showResumePanel ? (
              <ResumeScreeningConfigPanel
                config={resumeConfig}
                onChange={setResumeConfig}
              />
            ) : showCustomPanel ? (
              <CustomQuestionsPanel
                questions={customQuestions}
                onDeleteQuestion={handleDeleteCustomQuestion}
                onEditQuestion={(q) => setEditingQuestion(q)}
                onReorderQuestions={handleReorderCustomQuestions}
                onOpenAddModal={() => setShowAddQuestionModal(true)}
                onOpenGenerateModal={() => setShowGenerateModal(true)}
              />
            ) : showEditorPanel ? (
              <>
                <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-white shrink-0">
                  <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">
                    {displayLabels[activeId] ?? "Assessment"}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowQuestionLibrary(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400 hover:text-zinc-900 transition-all"
                    style={{ borderRadius: 0 }}
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                    <span className="text-[11px] font-bold uppercase tracking-widest">
                      Question Library
                    </span>
                  </button>
                </div>

                <div className="flex-1 relative min-h-0">
                  {assessments.map((a) => {
                    const ref = getEditorRef(a.id)
                    return (
                      <div
                        key={a.id}
                        className="absolute inset-0 flex flex-col"
                        style={{ display: activeId === a.id ? "flex" : "none" }}
                      >
                        <RichEditor
                          editorRef={ref}
                          savedContent={editorContents[a.id] ?? ""}
                          placeholder={
                            a.category
                              ? `Add questions or context for ${a.category}…`
                              : "Describe the assessment — add questions, instructions, or context for the AI interviewer…"
                          }
                          onInput={(html) =>
                            setEditorContents((prev) => ({
                              ...prev,
                              [a.id]: html,
                            }))
                          }
                        />
                      </div>
                    )
                  })}
                </div>
              </>
            ) : (
              /* Default empty state — no assessments and not showing custom panel */
              <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-8">
                <div className="w-10 h-10 border border-zinc-200 flex items-center justify-center bg-zinc-50">
                  <Plus size={16} className="text-zinc-300" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                    No assessment selected
                  </p>
                  <p className="text-[11px] text-zinc-300 mt-1 leading-snug">
                    Select "Custom Questions" or add a skill-based assessment on
                    the left
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-zinc-200 bg-white flex items-center justify-between shrink-0">
          <p
            className="text-[13px] text-zinc-500"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            Saving will overwrite the existing round configuration.
          </p>

          <button
            disabled={isSubmitting}
            onClick={handleSubmit}
            className={`flex items-center gap-2 px-5 py-2.5 text-xs font-bold tracking-wide transition-all border ${
              isSubmitting
                ? "bg-zinc-400 border-zinc-400 text-white cursor-not-allowed"
                : "bg-zinc-900 border-zinc-900 text-white hover:bg-zinc-800"
            }`}
            style={{ borderRadius: 0 }}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                Saving…
              </>
            ) : (
              <>
                Save Changes
                <ChevronRight size={12} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Category Picker Modal */}
      {showCategoryPicker && (
        <CategoryPickerModal
          onSelect={handleCategorySelect}
          onClose={() => setShowCategoryPicker(false)}
        />
      )}

      {showInstructionsModal && (
        <AdditionalInstructionsModal
          value={additionalInstructions}
          onSave={(val) => setAdditionalInstructions(val)}
          onClose={() => setShowInstructionsModal(false)}
        />
      )}

      {(showAddQuestionModal || editingQuestion) && (
        <AddQuestionModal
          initialQuestion={editingQuestion}
          onAdd={
            editingQuestion ? handleEditCustomQuestion : handleAddCustomQuestion
          }
          onClose={() => {
            setShowAddQuestionModal(false)
            setEditingQuestion(null)
          }}
        />
      )}

      {/* Question Library modal */}
      {showQuestionLibrary && (
        <QuestionLibrary
          onClose={() => setShowQuestionLibrary(false)}
          onAddQuestions={(questions) => {
            if (questions.length === 0) return
            const buildHtml = (q) => {
              const p = q.preview
              return [
                `<p><strong>${q.title}</strong></p>`,
                `<p><em>${q.difficulty} · ${q.topic}</em></p>`,
                p.problemStatement ? `<p>${p.problemStatement}</p>` : "",
                p.inputFormat
                  ? `<p><strong>Input:</strong> ${p.inputFormat}</p>`
                  : "",
                p.outputFormat
                  ? `<p><strong>Output:</strong> ${p.outputFormat}</p>`
                  : "",
                p.example
                  ? `<p><strong>Example:</strong> ${p.example}</p>`
                  : "",
              ]
                .filter(Boolean)
                .join("")
            }
            const activeEl = editorRefs.current[activeId]?.current
            const activeIsEmpty = !activeEl || activeEl.innerHTML.trim() === ""
            const assignments = []
            questions.forEach((q, idx) => {
              const questionCategory = CATEGORIES.includes(q.category)
                ? q.category
                : null
              if (idx === 0 && activeIsEmpty) {
                assignments.push({
                  id: activeId,
                  html: buildHtml(q),
                  category: questionCategory,
                })
              } else {
                const newId = nextId.current++
                assignments.push({
                  id: newId,
                  html: buildHtml(q),
                  category: questionCategory,
                })
              }
            })
            const lastId = assignments[assignments.length - 1].id
            setAssessments((prev) => {
              const existingIds = new Set(prev.map((a) => a.id))
              const newVariations = assignments
                .filter((a) => !existingIds.has(a.id))
                .map((a) => ({ id: a.id, category: a.category }))
              return prev
                .map((a) => {
                  const match = assignments.find((x) => x.id === a.id)
                  return match ? { ...a, category: match.category } : a
                })
                .concat(newVariations)
            })
            setEditorContents((prev) => {
              const next = { ...prev }
              assignments.forEach(({ id }) => {
                if (!(id in next)) next[id] = ""
              })
              return next
            })
            setActiveId(lastId)
            setRightPanel("editor")
            setShowQuestionLibrary(false)
            const inject = () => {
              const contentUpdates = {}
              assignments.forEach(({ id, html }) => {
                if (!editorRefs.current[id])
                  editorRefs.current[id] = { current: null }
                const el = editorRefs.current[id]?.current
                if (el) el.innerHTML = html
                contentUpdates[id] = html
              })
              setEditorContents((prev) => ({ ...prev, ...contentUpdates }))
              const lastEl = editorRefs.current[lastId]?.current
              if (lastEl) {
                lastEl.focus()
                const range = document.createRange()
                range.selectNodeContents(lastEl)
                range.collapse(false)
                const sel = window.getSelection()
                sel.removeAllRanges()
                sel.addRange(range)
              }
            }
            requestAnimationFrame(() => requestAnimationFrame(inject))
          }}
        />
      )}

      {showGenerateModal && (
        <GenerateQuestionModal
          onClose={() => setShowGenerateModal(false)}
          onAddQuestions={(questions) => {
            if (questions.length === 0) return

            // If we're in custom panel, add to custom questions
            if (rightPanel === "custom") {
              questions.forEach((q) => {
                const id = nextId.current++
                setCustomQuestions((prev) => [
                  ...prev,
                  {
                    id,
                    text:
                      q.question?.replace(/<[^>]+>/g, " ").trim() ?? q.question,
                  },
                ])
              })
              setShowGenerateModal(false)
              return
            }

            // Otherwise, add as skill-based assessments (original behaviour)
            const activeEl = editorRefs.current[activeId]?.current
            const activeIsEmpty = !activeEl || activeEl.innerHTML.trim() === ""
            const assignments = []

            questions.forEach((q, idx) => {
              const questionCategory = CATEGORIES.includes(q.category)
                ? q.category
                : null
              if (idx === 0 && activeIsEmpty && assessments.length > 0) {
                assignments.push({
                  id: activeId,
                  html: q.question,
                  category: questionCategory,
                })
              } else {
                const newId = nextId.current++
                assignments.push({
                  id: newId,
                  html: q.question,
                  category: questionCategory,
                })
              }
            })

            const lastId = assignments[assignments.length - 1].id

            setAssessments((prev) => {
              const existingIds = new Set(prev.map((a) => a.id))
              const newVariations = assignments
                .filter((a) => !existingIds.has(a.id))
                .map((a) => ({ id: a.id, category: a.category }))
              return prev
                .map((a) => {
                  const match = assignments.find((x) => x.id === a.id)
                  return match ? { ...a, category: match.category } : a
                })
                .concat(newVariations)
            })

            setEditorContents((prev) => {
              const next = { ...prev }
              assignments.forEach(({ id }) => {
                if (!(id in next)) next[id] = ""
              })
              return next
            })

            setActiveId(lastId)
            setRightPanel("editor")
            setShowGenerateModal(false)

            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                const updates = {}
                assignments.forEach(({ id, html }) => {
                  if (!editorRefs.current[id])
                    editorRefs.current[id] = { current: null }
                  const el = editorRefs.current[id]?.current
                  if (el) el.innerHTML = html
                  updates[id] = html
                })
                setEditorContents((prev) => ({ ...prev, ...updates }))
              }),
            )
          }}
        />
      )}

      {showSettingsModal && (
        <InterviewSettingsModal
          interviewMode="ai"
          initialDuration={interviewSettings.duration}
          initialSkills={interviewSettings.skills}
          onSave={(settings) => setInterviewSettings(settings)}
          onClose={() => setShowSettingsModal(false)}
        />
      )}
    </div>
  )
}

export default CreateAiInterview
